/* eslint-disable sonarjs/cognitive-complexity */
import * as React from 'react'
import ReactDOM from 'react-dom'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import tokens from '@lacedltd/design-tokens'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`

const Backdrop = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  zIndex: 9998,
  animation: `${fadeIn} 0.2s ease`
})

const Sheet = styled.div({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  background: tokens.color.yeezy[0],
  borderRadius: `${tokens.borderRadius.l} ${tokens.borderRadius.l} 0 0`,
  zIndex: 9999,
  animation: `${slideUp} 0.3s ease`,
  maxHeight: '85vh',
  overflowY: 'auto',
  touchAction: 'pan-y'
})

const DragHandle = styled.div({
  display: 'flex',
  justifyContent: 'center',
  padding: `${tokens.spacing[3]} 0 ${tokens.spacing[1]}`,
  cursor: 'grab',

  '&::after': {
    content: '""',
    width: '36px',
    height: '4px',
    borderRadius: '2px',
    background: tokens.color.airJordan[300]
  }
})

const ProductHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[4],
  padding: `0 ${tokens.spacing[5]} ${tokens.spacing[4]}`,
  borderBottom: `${tokens.borderWidth.xs} solid ${tokens.color.airJordan[200]}`,

  img: {
    width: tokens.spacing[10],
    height: tokens.spacing[10],
    objectFit: 'contain'
  }
})

const ProductInfo = styled.div({
  flex: 1,
  minWidth: 0
})

const ProductTitle = styled.div({
  fontSize: tokens.fontSize[2],
  fontWeight: 500,
  color: tokens.color.airJordan.p,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const
})

const ProductPrice = styled.div({
  fontSize: tokens.fontSize[1],
  color: tokens.color.airJordan[500],
  marginTop: tokens.spacing[1]
})

const SheetContent = styled.div({
  padding: `${tokens.spacing[4]} ${tokens.spacing[5]} ${tokens.spacing[6]}`
})

export interface BottomSheetProps {
  open: boolean
  onClose: () => void
  productTitle: string
  productImageUrl: string
  currentPrice: string
  children: React.ReactNode
}

const DISMISS_THRESHOLD = 100

const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  productTitle,
  productImageUrl,
  currentPrice,
  children
}) => {
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const dragStartY = React.useRef<number | null>(null)
  const dragStartX = React.useRef<number | null>(null)
  const isVerticalDrag = React.useRef<boolean | null>(null)
  const [dragOffset, setDragOffset] = React.useState(0)

  // Lock body scroll
  React.useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow

    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const handlePointerDown = (e: React.PointerEvent) => {
    // Only start drag tracking from the drag handle area or sheet top
    const target = e.target as HTMLElement

    if (target.closest('[data-drag-handle]')) {
      dragStartY.current = e.clientY
      dragStartX.current = e.clientX
      isVerticalDrag.current = null
      setDragOffset(0)
    }
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragStartY.current === null || dragStartX.current === null) return

    const dy = e.clientY - dragStartY.current
    const dx = e.clientX - dragStartX.current

    // Determine drag direction on first significant movement
    if (isVerticalDrag.current === null) {
      if (Math.abs(dy) > 5 || Math.abs(dx) > 5) {
        isVerticalDrag.current = Math.abs(dy) > Math.abs(dx)
      }

      return
    }

    if (!isVerticalDrag.current) return

    // Only allow dragging downward
    const offset = Math.max(0, dy)

    setDragOffset(offset)
  }

  const handlePointerUp = () => {
    if (dragStartY.current === null) return

    if (dragOffset > DISMISS_THRESHOLD) {
      onClose()
    }

    dragStartY.current = null
    dragStartX.current = null
    isVerticalDrag.current = null
    setDragOffset(0)
  }

  if (!open) return null

  const portalRoot = document.getElementById('modal-root')

  if (!portalRoot) return null

  return ReactDOM.createPortal(
    <>
      <Backdrop onClick={onClose} />
      <Sheet
        ref={sheetRef}
        style={
          dragOffset > 0
            ? { transform: `translateY(${dragOffset}px)`, animation: 'none' }
            : undefined
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <DragHandle data-drag-handle />
        <ProductHeader>
          <img src={productImageUrl} alt={productTitle} />
          <ProductInfo>
            <ProductTitle>{productTitle}</ProductTitle>
            <ProductPrice>Current: {currentPrice}</ProductPrice>
          </ProductInfo>
        </ProductHeader>
        <SheetContent>{children}</SheetContent>
      </Sheet>
    </>,
    portalRoot
  )
}

export default BottomSheet
